import React from "react";
import { useTheme } from "../contexts/ThemeContext.tsx";
import { getTheme } from "../theme.ts";

interface PaginationProps {
  currentPage?: number;
  totalPages?: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage=1 , totalPages=1, onPageChange }) => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-center space-x-2 p-2 shadow-md border-t" style={{backgroundColor: currentTheme.surface, borderColor: currentTheme.border}}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-2 py-1 rounded-lg text-xs border transition ${
          currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
        }`}
        style={{
          borderColor: currentTheme.primary,
          color: currentTheme.primary,
          backgroundColor: currentTheme.surface
        }}
        onMouseEnter={(e) => {
          if (currentPage !== 1) {
            e.currentTarget.style.backgroundColor = currentTheme.primary;
            e.currentTarget.style.color = currentTheme.inverseText;
          }
        }}
        onMouseLeave={(e) => {
          if (currentPage !== 1) {
            e.currentTarget.style.backgroundColor = currentTheme.surface;
            e.currentTarget.style.color = currentTheme.primary;
          }
        }}
      >
        ← Prev
      </button>

      {getPageNumbers().map((page, index) =>
        typeof page === "number" ? (
          <button
            key={index}
            onClick={() => onPageChange(page)}
            className="px-2 py-1 rounded-lg text-xs transition"
            style={{
              backgroundColor: page === currentPage ? currentTheme.primary : currentTheme.surface,
              color: page === currentPage ? currentTheme.inverseText : currentTheme.text,
              border: page === currentPage ? 'none' : `1px solid ${currentTheme.border}`,
              fontWeight: page === currentPage ? 'semibold' : 'normal',
              boxShadow: page === currentPage ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
            }}
            onMouseEnter={(e) => {
              if (page !== currentPage) {
                e.currentTarget.style.backgroundColor = currentTheme.primary;
                e.currentTarget.style.color = currentTheme.inverseText;
              }
            }}
            onMouseLeave={(e) => {
              if (page !== currentPage) {
                e.currentTarget.style.backgroundColor = currentTheme.surface;
                e.currentTarget.style.color = currentTheme.text;
              }
            }}
          >
            {page}
          </button>
        ) : (
          <span key={index} className="px-2 py-1 text-xs" style={{color: currentTheme.textSecondary}}>...</span>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-2 py-1 rounded-lg text-xs border transition ${
          currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""
        }`}
        style={{
          borderColor: currentTheme.primary,
          color: currentTheme.primary,
          backgroundColor: currentTheme.surface
        }}
        onMouseEnter={(e) => {
          if (currentPage !== totalPages) {
            e.currentTarget.style.backgroundColor = currentTheme.primary;
            e.currentTarget.style.color = currentTheme.inverseText;
          }
        }}
        onMouseLeave={(e) => {
          if (currentPage !== totalPages) {
            e.currentTarget.style.backgroundColor = currentTheme.surface;
            e.currentTarget.style.color = currentTheme.primary;
          }
        }}
      >
        Next →
      </button>
    </div>
  );
};

export default Pagination;
